'use client';

import { useEffect, useRef, useState } from 'react';
import { isNil } from 'es-toolkit';
import { toast } from 'sonner';
import { PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION } from 'src/features/advisor-book-onboarding/api/advisor-book-onboarding-gql';
import {
  ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_QUERY,
  getAdvisorOnboardingStagedHousehold,
} from 'src/features/advisor-book-onboarding/api/confirm-accounts-gql';
import { blockedReasonLabelFor } from 'src/features/advisor-book-onboarding/constants/cockpit-status-copy';
import {
  clientNeedsNameNormalize,
  useStagedBookMutations,
} from 'src/features/advisor-book-onboarding/hooks/use-staged-book-mutations';
import { callPromoteAdvisorOnboardingEnrollment } from 'src/features/advisor-book-onboarding/utils/promote-advisor-onboarding-enrollment';
import {
  countSheetEditableContactErrors,
  getClientFullName,
  getCoClients,
  getPrimaryClient,
} from 'src/features/advisor-book-onboarding/utils/staged-book';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

const RESOLVE_FALLBACK_MESSAGE = 'Failed to resolve contact info, please try again';

type UseFixContactInfoParams = {
  readonly stagedHouseholdUuid: string | null;
  readonly open: boolean;
  readonly onResolved: () => void;
};

function stillBlockedMessage(blockedReason: string | null | undefined) {
  if (blockedReason === 'missing_client_info') {
    return 'Contact info was saved, but validation still failed. Fix the fields marked Missing or invalid and try again.';
  }
  if (!isNil(blockedReason) && blockedReason !== '') {
    return `Contact info was saved, but this household is still blocked (${blockedReasonLabelFor(blockedReason)}).`;
  }
  return 'Contact info was saved, but this household is still blocked. Check the fields and try again.';
}

export function useFixContactInfo({ stagedHouseholdUuid, open, onResolved }: UseFixContactInfoParams) {
  const [isResolving, setIsResolving] = useState(false);
  const resolveInFlightRef = useRef(false);
  const normalizedClientIdsRef = useRef<ReadonlySet<string>>(new Set());
  const normalizeInFlightRef = useRef<ReadonlySet<string>>(new Set());

  const skip = !open || stagedHouseholdUuid === null;

  const { data, error, refetch } = useQueryWithErrorLogging(ADVISOR_ONBOARDING_STAGED_HOUSEHOLD_QUERY, {
    variables: { id: stagedHouseholdUuid ?? '' },
    skip,
    fetchPolicy: 'network-only',
  });

  const parsedHousehold = getAdvisorOnboardingStagedHousehold(data);
  const isStaleHousehold =
    !isNil(parsedHousehold) && stagedHouseholdUuid !== null && parsedHousehold.uuid !== stagedHouseholdUuid;
  const household = isStaleHousehold ? undefined : parsedHousehold;
  // Ignore Apollo `loading` while we already have the requested row — blur-save refetch
  // would otherwise flash skeletons and remount the editable fields.
  const isLoading = !skip && isNil(error) && (isStaleHousehold || parsedHousehold === undefined);
  const primaryClient = !isNil(household) ? getPrimaryClient(household) : undefined;
  const coClients = !isNil(household) ? getCoClients(household) : [];
  const contactErrorCount = !isNil(household) ? countSheetEditableContactErrors(household) : 0;
  const enrollmentUuid = household?.enrollmentUuid;
  const canResolve =
    !isNil(household) &&
    !isNil(primaryClient) &&
    !isNil(enrollmentUuid) &&
    enrollmentUuid !== '' &&
    contactErrorCount === 0 &&
    !isResolving;

  const mutations = useStagedBookMutations({
    refetchStagedBook: async () => {
      await refetch();
    },
  });
  const { updateStagedClientName } = mutations;
  const updateStagedClientNameRef = useRef(updateStagedClientName);
  updateStagedClientNameRef.current = updateStagedClientName;

  const [promoteEnrollment] = useMutationWithErrorLogging(PROMOTE_ADVISOR_ONBOARDING_ENROLLMENT_MUTATION);

  useEffect(() => {
    if (!open) {
      normalizedClientIdsRef.current = new Set();
      normalizeInFlightRef.current = new Set();
    }
  }, [open]);

  useEffect(() => {
    if (isNil(household) || isLoading) {
      return;
    }

    const clientsToNormalize = household.stagedClients.filter(
      (client) =>
        clientNeedsNameNormalize(client) &&
        !normalizedClientIdsRef.current.has(client.id) &&
        !normalizeInFlightRef.current.has(client.id),
    );
    if (clientsToNormalize.length === 0) {
      return;
    }

    normalizeInFlightRef.current = new Set([
      ...normalizeInFlightRef.current,
      ...clientsToNormalize.map((client) => client.id),
    ]);

    void Promise.all(
      clientsToNormalize.map(async (client) => {
        const succeeded = await updateStagedClientNameRef.current({
          clientId: client.id,
          fullName: getClientFullName(client),
          originalFirstName: client.firstName,
          originalLastName: client.lastName,
        });
        if (succeeded) {
          normalizedClientIdsRef.current = new Set([...normalizedClientIdsRef.current, client.id]);
        }
        const nextInFlight = new Set(normalizeInFlightRef.current);
        nextInFlight.delete(client.id);
        normalizeInFlightRef.current = nextInFlight;
      }),
    );
  }, [household, isLoading]);

  async function resolve() {
    if (!canResolve || isNil(enrollmentUuid) || resolveInFlightRef.current) {
      return;
    }

    resolveInFlightRef.current = true;
    setIsResolving(true);

    const promoteResult = await callPromoteAdvisorOnboardingEnrollment({
      promoteEnrollment,
      enrollmentUuid,
      fallbackMessage: RESOLVE_FALLBACK_MESSAGE,
    });

    if (promoteResult.isErr()) {
      const failure = promoteResult.error;
      if (failure.cause !== undefined) {
        handleMutationError(failure.cause, failure.message);
      } else {
        toast.error(failure.message);
      }
      resolveInFlightRef.current = false;
      setIsResolving(false);
      return;
    }

    try {
      const refetchResult = await refetch();
      const updatedHousehold = getAdvisorOnboardingStagedHousehold(refetchResult.data);
      if (isNil(updatedHousehold) || updatedHousehold.status === 'blocked') {
        toast.error(stillBlockedMessage(updatedHousehold?.blockedReason));
        return;
      }
      onResolved();
    } catch (refetchError) {
      handleMutationError(refetchError, RESOLVE_FALLBACK_MESSAGE);
    } finally {
      resolveInFlightRef.current = false;
      setIsResolving(false);
    }
  }

  return {
    household,
    primaryClient,
    coClients,
    contactErrorCount,
    canResolve,
    isResolving,
    isLoading,
    loadError: error,
    mutations,
    resolve,
    refetch,
  } as const;
}
